import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useCallback, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { match } from 'ts-pattern'

import Footer from '@/components-latest/layout/Footer/Footer'
import { SmallFooter } from '@/components-latest/layout/SmallFooter/SmallFooter'
import Label from '@/components-latest/primitives/Label'
import SplashScreen from '@/components-latest/primitives/SplashScreen'
import Text from '@/components-latest/primitives/Text'
import DirectionProvider from '@/components-latest/shared/DirectionProvider'
import { LanguageSelectSimple } from '@/components-latest/shared/LanguageSelect'
import RegistrationForm, {
  RegistrationFormData
} from '@/components-latest/ui/RegistrationPage/RegistrationForm'
import { ENVIRONMENT } from '@/constants/env'
import { FOOD_DEPLOYS } from '@/constants/links'
import {
  useFetchMarketPresenceQuery,
  useRegisterStoreMutation,
  useSendRestaurantVerificationEmailMutation
} from '@/ducks/registration/api/registration.api'
import { ApiRegistrationError } from '@/ducks/registration/registration.types'
import { useDeviceSize } from '@/hooks/useDeviceSize/useDeviceSize'

import Styles from './RegistrationPage.module.scss'
const RegistrationPage = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation()
  const marketPresenceQuery = useFetchMarketPresenceQuery()

  const { lmd_store_webapp_registration_page } = useFlags()

  const { isMobile } = useDeviceSize()

  const [registerStore, { isLoading, error }] =
    useRegisterStoreMutation()
  
  const [sendVerificationEmail] = useSendRestaurantVerificationEmailMutation()
  
  const [isVerified, setIsVerified] = useState(false)
  const [registrationResult, setRegistrationResult] = useState<any>(null)
  const [initialFormData, setInitialFormData] =
    useState<RegistrationFormData | null>(null)
  const [sendVerificationError, setSendVerificationError] =
    useState<ApiRegistrationError | null>(null)

  // Check if user returned from verification page with success
  useEffect(() => {
    const state = location.state as {
      verified?: boolean
      registrationData?: any
      formData?: RegistrationFormData
    } | null
    if (state?.verified && state?.registrationData) {
      setIsVerified(true)
      setRegistrationResult(state.registrationData)
      // Clear the state to prevent re-triggering
      window.history.replaceState({}, document.title)
      return
    }
    if (state?.formData) {
      setInitialFormData(state.formData)
    }
  }, [location.state])

  const handleSubmit = useCallback(
    async (formData: RegistrationFormData) => {
      // Send OTP email and navigate to verification page
      try {
        await sendVerificationEmail({
          email: formData.email,
          phone: formData.phone,
          userName: formData.username
        }).unwrap()
        setSendVerificationError(null)
        
        // Navigate to verification page with registration data
        history.push('/registration/verify-email', {
          email: formData.email,
          userName: formData.username,
          registrationData: formData
        })
      } catch (err: unknown) {
        const normalizedError =
          (err as { data?: ApiRegistrationError })?.data ||
          (err as ApiRegistrationError)
        setSendVerificationError(normalizedError)
      }
    },
    [sendVerificationEmail, history]
  )

  /*  code below should be in app root
   *  at the moment we don't support other rtl pages except registration page
   *  TODO: apply rtl and rtl provider at app root
   * */

  if (!lmd_store_webapp_registration_page) {
    window.location.replace(
      `${FOOD_DEPLOYS[ENVIRONMENT]}/restaurant/registration`
    )
    return null
  }

  return (
    //   DirectionProvider should be in app root as well
    <DirectionProvider>
      <div className={Styles.registrationPage} data-testid="registration-page">
        {isVerified && registrationResult ? (
          <div className={Styles.successScreen}>
            <div className={Styles.checkWrapper}>
              <img
                className={Styles.successCheck}
                alt="logo"
                src="/images/registration-check.svg"
              />
            </div>
            <Text variant="nextTitle-2" gutter={8}>
              {t('registration.page.successTitle')}
            </Text>
            <Text variant="nextSubtitle" gutter={40}>
              {t('registration.page.successDescription')}
            </Text>
          </div>
        ) : (
          <>
            <Link to="/" className={Styles.logoWrapper}>
              <img
                className={Styles.logo}
                alt="logo"
                src="/images/yassir-express-text-logo.svg"
              />
            </Link>
            {!isMobile && (
              <Label layout="column" className={Styles.languageSelect}>
                <Text
                  variant="nextSubtitle"
                  weight="bold"
                  className={Styles.label}>
                  {t('registration.languageSelect.label')}
                </Text>
                <LanguageSelectSimple />
              </Label>
            )}
            <div className={Styles.form}>
              <Text variant="nextTitle-2" gutter={8}>
                {t('registration.page.title')}
              </Text>
              <Text variant="nextSubtitle" gutter={40}>
                {t('registration.page.description')}
              </Text>
              {match(marketPresenceQuery)
                .with({ isLoading: true }, () => <SplashScreen />)
                .with({ isSuccess: true }, ({ data }) => (
                  <RegistrationForm
                    onSubmit={handleSubmit}
                    isSubmitting={isLoading}
                    countries={data.countries}
                    cities={data.cities}
                    cuisines={data.cuisines}
                    error={
                      (sendVerificationError ||
                        (error as ApiRegistrationError)) ??
                      undefined
                    }
                    initialValues={initialFormData ?? undefined}
                  />
                ))
                .with({ isError: true }, ({ error }) => (
                  <div>
                    {(typeof error === 'object' && error.message) ||
                      'Something went wrong'}
                  </div>
                ))
                .otherwise(() => null)}
            </div>
          </>
        )}
      </div>
      <Footer />
      <SmallFooter />
    </DirectionProvider>
  )
}

export default RegistrationPage
